import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { memo, ReactNode, useMemo } from 'react'

interface FaqItem {
  question: string
  answer: string | ReactNode
}

const FaqItemComponent = memo(function FaqItemComponent({ question, answer }: FaqItem) {
  return (
    <Disclosure>
      {({ open }) => (
        <div className='border border-[#cecece] rounded-lg overflow-hidden'>
          <DisclosureButton className='w-full text-left px-6 py-4'>
            <div className='flex justify-between items-center w-full'>
              <p className='font-semibold text-base text-gray-12 leading-[1.6] flex-1'>
                {question}
              </p>
              <div className='flex items-center justify-center flex-shrink-0'>
                {open ? (
                  <ExpandLessIcon className='w-6 h-6 text-[#8d8d8d] transition-transform' />
                ) : (
                  <ExpandMoreIcon className='w-6 h-6 text-[#8d8d8d] transition-transform' />
                )}
              </div>
            </div>
          </DisclosureButton>
          <Transition
            enter='transition duration-100 ease-out'
            enterFrom='transform scale-95 opacity-0'
            enterTo='transform scale-100 opacity-100'
            leave='transition duration-75 ease-out'
            leaveFrom='transform scale-100 opacity-100'
            leaveTo='transform scale-95 opacity-0'
          >
            <DisclosurePanel>
              <div className='px-6 pb-4 font-normal text-sm text-gray-12 leading-[1.6] tracking-[-0.24px]'>
                {typeof answer === 'string' ? <p>{answer}</p> : answer}
              </div>
            </DisclosurePanel>
          </Transition>
        </div>
      )}
    </Disclosure>
  )
})

export default function DPFFaqJpSection() {
  const faqItems = useMemo<FaqItem[]>(
    () => [
      {
        question: 'Wi-Fiは必要ですか？',
        answer: 'はい。写真や動画を受け取るためにWi-Fi接続が必要です。',
      },
      {
        question: '動画も見られますか？音は出ますか？',
        answer:
          'はい。音付きの動画を再生できます。動画の再生にはプレミアムプランへの加入が必要です。音量は設定画面から調整できます。早送りの操作はできません。',
      },
      {
        question: '端末の購入以外にサブスクへの登録は必要ですか？',
        answer:
          'いいえ、端末の購入だけでご利用いただけます。ただし、みてねプレミアム未加入の場合は、みてねからおすすめの写真11枚のみが月替わりで表示され、動画の表示や11枚以上の写真の表示はできません。写真をご自身で選択することもできません。すでにみてねプレミアムに登録されている場合や、新しく加入された場合は、すべての機能と特典をご利用いただけます。',
      },
      {
        question:
          'プレミアムを契約していますが、アルバムに参加している他の家族は追加で契約しなくてもプレミアムの機能が利用できますか？',
        answer:
          'はい。プレミアムは「アルバム単位」で適用される機能です。そのため、プレミアムを契約しているアルバムに参加されている方であれば、どなたでもプレミアムの機能をご利用いただけます。たとえば、お客様がプレミアム契約をしているアルバムのオーナーで、そのアルバムにおじいさま・おばあさまが参加されている場合、おじいさま・おばあさまがご自身のアカウントとフォトフレームを連携することで、プレミアムの機能がご利用いただけます。',
      },
      {
        question: 'みてねプレミアムを解約したらどうなりますか？',
        answer:
          '無制限に写真を表示するなどのプレミアムの特典はご利用いただけなくなり、みてねからのおすすめ写真11枚のみが月替わりで表示されるようになります。動画の表示や11枚以上の写真の表示、写真をご自身で選択することはできません。',
      },
      {
        question: '壁掛けはできますか？',
        answer:
          'はい。ネジを別途ご用意のうえ、フレーム背面にある穴を使って壁に掛けることができます。また、付属のスタンドで机や棚の上にも設置できます。※フレームは常に電源に接続して使用する必要があります。',
      },
      {
        question: '祖父母など、離れて暮らす家族へプレゼントすることはできますか？',
        answer: (
          <>
            <p className='font-normal'>
              はい、できます。
              <br />
              アカウントの連携は次の2つのパターンがあります。
            </p>
            <br />
            <p>
              <span className='font-semibold block'>
                受け取る方ご本人のアカウントで連携する場合
              </span>
              受け取る方がみてねのアカウントをお持ちの場合はこの方法がご利用いただけます。フォトフレームを直接お贈りし、受け取る方ご自身がフレームと連携することでお使い頂けます。みてね上の公開範囲がそのまま引き継がれるため、受け取る方がみてねアプリ上で見ることができる写真や動画のみがフォトフレームに表示されます。
            </p>
            <br />
            <p>
              <span className='font-semibold block'>贈る方のアカウントで連携する場合</span>
              受け取る方がみてねのアカウントをお持ちでなくてもご利用いただけます。この場合は、贈る方がまずは設定を行い、その後フレームをお贈りください。この場合、贈る方のアカウントで見ることができるすべての写真・動画がフレームに表示される対象となります。※どちらの方法でも、受け取る方のご利用場所にWi-Fi環境が必要です。
            </p>
          </>
        ),
      },
      {
        question: 'みてねで複数のアルバムに参加している場合、どのように表示されますか？',
        answer:
          '初期設定時に、それぞれのアルバムから表示する設定にしておくことで、アルバムを切り替えることなく、複数のアルバムの写真や動画をまとめて見ることができます。なお、プレミアムに加入しているアルバムのみプレミアム特典を利用でき、無料のアルバムには表示枚数や機能の制限があります。',
      },
      {
        question: 'USBメモリ等の外付け機器を使うことはできますか？',
        answer:
          'いいえ、使用できません。フレーム本体に差し込み口はありますが、USBメモリを含む外付け機器はすべて利用できない仕様になっています。',
      },
      {
        question: 'コードレスで利用することはできますか？',
        answer:
          'いいえ、バッテリーは内蔵されていないため、ご利用の際は常に電源に接続する必要があります。',
      },
      {
        question: '縦型写真は表示できますか？',
        answer:
          'はい、表示できます。1枚の縦型写真を中央に配置するモードと、2枚の縦型写真を並べて表示するモードの2つの表示方法からお好きな方を選ぶことができます。',
      },
      {
        question: 'フォトフレームを縦に置くことはできますか？',
        answer: 'いいえ、できません。縦向きに設置しても、写真は横向きのまま表示されます。',
      },
      {
        question: '色はウォームグレーの1種類のみですか？',
        answer: 'はい、現在はウォームグレーの1種類のみのご用意となります。',
      },
    ],
    [],
  )

  return (
    <section className='px-4 pt-10 pb-8'>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2 items-center'>
          <h2 className='font-semibold text-2xl text-gray-12 text-center leading-[1.4]'>
            よくある質問
          </h2>
        </div>
        <div className='flex flex-col gap-2'>
          {faqItems.map(item => (
            <FaqItemComponent key={item.question} question={item.question} answer={item.answer} />
          ))}
        </div>
      </div>
    </section>
  )
}
